import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    if (!isHome) return; // Let Link handle navigation
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-[#020204]/90 backdrop-blur-xl border-b border-white/[0.04]'
          : 'bg-transparent'
      }`}
    >
      <nav className="container mx-auto px-6 py-5">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group"
          >
            <div className="w-8 h-8 border border-[#00ffd5]/40 flex items-center justify-center group-hover:border-[#00ffd5] transition-colors">
              <div className="w-2 h-2 bg-[#00ffd5]"></div>
            </div>
            <span className="font-mono text-sm font-semibold tracking-[0.2em] text-white/90 uppercase group-hover:text-[#00ffd5] transition-colors">
              COZMOZ AI
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {isHome ? (
              <>
                <a
                  href="#how-it-works"
                  onClick={(e) => handleNavClick(e, '#how-it-works')}
                  className="font-mono text-[0.7rem] tracking-[0.15em] uppercase text-white/35 hover:text-[#00ffd5] transition-colors"
                >
                  Process
                </a>
                <a
                  href="#team"
                  onClick={(e) => handleNavClick(e, '#team')}
                  className="font-mono text-[0.7rem] tracking-[0.15em] uppercase text-white/35 hover:text-[#00ffd5] transition-colors"
                >
                  Team
                </a>
              </>
            ) : (
              <>
                <Link
                  to="/#how-it-works"
                  className="font-mono text-[0.7rem] tracking-[0.15em] uppercase text-white/35 hover:text-[#00ffd5] transition-colors"
                >
                  Process
                </Link>
                <Link
                  to="/#team"
                  className="font-mono text-[0.7rem] tracking-[0.15em] uppercase text-white/35 hover:text-[#00ffd5] transition-colors"
                >
                  Team
                </Link>
              </>
            )}
            <Link
              to="/blog"
              className={`font-mono text-[0.7rem] tracking-[0.15em] uppercase transition-colors ${
                location.pathname.startsWith('/blog')
                  ? 'text-[#00ffd5]'
                  : 'text-white/35 hover:text-[#00ffd5]'
              }`}
            >
              Blog
            </Link>
            <button
              className="btn-primary"
              onClick={() => window.open('https://cal.com/mahima-arora-0f2025/30min?overlayCalendar=true', '_blank')}
            >
              Request Demo
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-white/50 hover:text-[#00ffd5]"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-6 pb-6 border-t border-white/[0.06] pt-6 space-y-4">
            {isHome ? (
              <>
                <a
                  href="#how-it-works"
                  onClick={(e) => handleNavClick(e, '#how-it-works')}
                  className="block font-mono text-[0.7rem] tracking-[0.15em] uppercase text-white/35 hover:text-[#00ffd5]"
                >
                  Process
                </a>
                <a
                  href="#team"
                  onClick={(e) => handleNavClick(e, '#team')}
                  className="block font-mono text-[0.7rem] tracking-[0.15em] uppercase text-white/35 hover:text-[#00ffd5]"
                >
                  Team
                </a>
              </>
            ) : (
              <>
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block font-mono text-[0.7rem] tracking-[0.15em] uppercase text-white/35 hover:text-[#00ffd5]"
                >
                  Process
                </Link>
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block font-mono text-[0.7rem] tracking-[0.15em] uppercase text-white/35 hover:text-[#00ffd5]"
                >
                  Team
                </Link>
              </>
            )}
            <Link
              to="/blog"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block font-mono text-[0.7rem] tracking-[0.15em] uppercase transition-colors ${
                location.pathname.startsWith('/blog')
                  ? 'text-[#00ffd5]'
                  : 'text-white/35 hover:text-[#00ffd5]'
              }`}
            >
              Blog
            </Link>
            <button
              className="btn-primary w-full mt-2"
              onClick={() => window.open('https://cal.com/mahima-arora-0f2025/30min?overlayCalendar=true', '_blank')}
            >
              Request Demo
            </button>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
